import {
  AnalyticsPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
} from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { marketingAnalyticsCustomReportsFlag } from "@redotech/redo-marketing-common/feature-flags";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { useTeamFeatureFlag } from "@redotech/redo-merchant-app-common/use-team-feature-flag";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import BarChart10FilledIcon from "@redotech/redo-web/icon-old/bar-chart-10-filled.svg";
import BarChart10Icon from "@redotech/redo-web/icon-old/bar-chart-10.svg";
import { memo } from "react";
import { useMatch } from "react-router-dom";

export const AnalyticsItems = memo(function AnalyticsItems() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const emailSmsEnabled = useCapability("emailSms");
  const orderTrackingEnabled = useCapability("orderTracking");

  const canViewMarketingAnalytics = useHasPermission(
    MarketingPermissions.VIEW_MARKETING_ANALYTICS,
  );
  const canViewOrderTrackingAnalytics = useHasPermission(
    MarketingPermissions.VIEW_ORDER_TRACKING_ANALYTICS,
  );
  const canViewReports = useHasPermission(
    OrderManagementPermissions.VIEW_REPORTS,
  );
  const canViewCustomAnalytics = useHasPermission(
    AnalyticsPermissions.VIEW_ANALYTICS,
  );
  const { enabled: marketingCustomReportsEnabled } = useTeamFeatureFlag(
    marketingAnalyticsCustomReportsFlag,
  );

  const showMarketing = emailSmsEnabled && canViewMarketingAnalytics;
  const showOrderTracking =
    orderTrackingEnabled && canViewOrderTrackingAnalytics;
  const showRecover =
    !!team.settings.recover?.enabled && canViewMarketingAnalytics;
  const showReports =
    canViewReports && canViewCustomAnalytics && marketingCustomReportsEnabled;

  const shouldShowAnalytics =
    showMarketing || showOrderTracking || showRecover || showReports;

  const defaultRoute = showMarketing
    ? routes.marketingAnalyticsHref
    : showOrderTracking
      ? `${routes.marketingBaseUrl}/analytics/order-tracking`
      : showRecover
        ? `${routes.marketingBaseUrl}/analytics/recover`
        : showReports
          ? routes.marketingAnalyticsReportsHref
          : "";

  const analyticsRoute = `${routes.marketingBaseUrl}/analytics`;
  const anyChildSelected = useMatch(`${analyticsRoute}/*`);

  if (!shouldShowAnalytics) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: defaultRoute }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={BarChart10FilledIcon}
      friendlyName={t("Analytics", {
        context: "merchant marketing navigation label",
      })}
      id="analytics"
      UnfilledIcon={BarChart10Icon}
    >
      <MarketingAnalyticsCustomReportsItem hidden={!showReports} />
      <MarketingAnalyticsMarketingItem hidden={!showMarketing} />
      <MarketingAnalyticsOrderTrackingItem hidden={!showOrderTracking} />
      <MarketingAnalyticsRecoverItem hidden={!showRecover} />
    </RedoSecondaryNavItem>
  );
});

const MarketingAnalyticsCustomReportsItem = memo(
  function MarketingAnalyticsCustomReportsItem({
    hidden,
  }: {
    hidden: boolean;
  }) {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.marketingAnalyticsReportsHref }}
        beta
        friendlyName={t("Custom reports", {
          context: "merchant marketing navigation label",
        })}
        hidden={hidden}
        id="analytics-reports"
      />
    );
  },
);

const MarketingAnalyticsMarketingItem = memo(
  function MarketingAnalyticsMarketingItem({ hidden }: { hidden: boolean }) {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.marketingAnalyticsHref }}
        friendlyName={t("Marketing", {
          context: "merchant marketing navigation label",
        })}
        hidden={hidden}
        id="analytics-marketing"
      />
    );
  },
);

const MarketingAnalyticsOrderTrackingItem = memo(
  function MarketingAnalyticsOrderTrackingItem({
    hidden,
  }: {
    hidden: boolean;
  }) {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.marketingBaseUrl}/analytics/order-tracking`,
        }}
        friendlyName={t("Order tracking", {
          context: "merchant marketing navigation label",
        })}
        hidden={hidden}
        id="analytics-order-tracking"
      />
    );
  },
);

const MarketingAnalyticsRecoverItem = memo(
  function MarketingAnalyticsRecoverItem({ hidden }: { hidden: boolean }) {
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.marketingBaseUrl}/analytics/recover`,
        }}
        friendlyName="Recover"
        hidden={hidden}
        id="analytics-recover"
      />
    );
  },
);
